import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLang } from "@/i18n/LanguageContext";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { enUS, hr, de, it } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOCALES = { en: enUS, hr, de, it };

function toYMD(d) {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseYMD(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map((x) => parseInt(x, 10));
  return new Date(y, m - 1, d);
}

function eachDayInRange(start, endExclusive) {
  // returns array of Date objects from start (inclusive) up to endExclusive (exclusive)
  const out = [];
  const cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (cur < endExclusive) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function formatMoney(amount, currency = "EUR", lang = "en") {
  try {
    return new Intl.NumberFormat(lang, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${Math.round(amount)}`;
  }
}

const PricingPanel = ({ t, lang, pricing, quote, loading, nights }) => {
  const currency = quote?.currency || pricing?.currency || "EUR";
  const base = quote?.base_nightly_price ?? pricing?.base_nightly_price;
  const discountPct =
    pricing?.weekly_discount_percent ?? quote?.discount_percent ?? 0;
  const minNights = pricing?.weekly_discount_min_nights ?? 7;

  // No dates selected → show "from €X / night"
  if (!quote) {
    return (
      <div
        data-testid="pricing-panel-teaser"
        className="border-t border-[#F8F5F0]/15 pt-6 flex items-center justify-between text-[#F8F5F0]/90"
      >
        <span className="text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60">
          {t.booking.priceFrom}
        </span>
        <span className="font-serif text-2xl text-[#F8F5F0]">
          {base != null ? formatMoney(base, currency, lang) : "—"}
          <span className="ml-2 text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60">
            / {t.booking.pricePerNight}
          </span>
        </span>
        {discountPct > 0 && (
          <span className="hidden sm:block text-[10px] uppercase tracking-[0.22em] text-[#C05C3D]">
            {t.booking.priceDiscountHint
              .replace("{n}", minNights)
              .replace("{p}", discountPct)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid="pricing-panel"
      className="border-t border-[#F8F5F0]/15 pt-6 space-y-3"
    >
      <div className="text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60">
        {t.booking.priceHeading}
      </div>
      {loading ? (
        <div className="text-sm text-[#F8F5F0]/70 flex items-center gap-2">
          <span className="inline-block w-4 h-4 border-2 border-[#F8F5F0]/30 border-t-[#C05C3D] rounded-full animate-spin" />
          {t.booking.priceLoading}
        </div>
      ) : (
        <>
          <div className="flex items-baseline justify-between text-sm text-[#F8F5F0]/80 font-light">
            <span>
              {formatMoney(quote.subtotal / Math.max(1, quote.nights), currency, lang)}{" "}
              × {quote.nights} {t.booking.nights}
            </span>
            <span data-testid="price-subtotal">
              {formatMoney(quote.subtotal, currency, lang)}
            </span>
          </div>
          {quote.weekly_discount_applied && quote.discount_amount > 0 && (
            <div
              data-testid="price-discount"
              className="flex items-baseline justify-between text-sm text-[#C05C3D] font-light"
            >
              <span>
                {t.booking.priceDiscount} · {quote.discount_percent}%
              </span>
              <span>− {formatMoney(quote.discount_amount, currency, lang)}</span>
            </div>
          )}
          <div className="flex items-baseline justify-between pt-3 border-t border-[#F8F5F0]/15">
            <span className="text-xs uppercase tracking-[0.24em] text-[#F8F5F0]/60 font-semibold">
              {t.booking.priceTotal}
            </span>
            <span
              data-testid="price-total"
              className="font-serif text-3xl text-[#F8F5F0]"
            >
              {formatMoney(quote.total, currency, lang)}
            </span>
          </div>
          {!quote.weekly_discount_applied && discountPct > 0 && (
            <div className="text-[11px] text-[#F8F5F0]/50 italic">
              {t.booking.priceDiscountHint
                .replace("{n}", minNights)
                .replace("{p}", discountPct)}
            </div>
          )}
          {quote.weekly_discount_applied && (
            <div className="text-[11px] text-[#8A9A86] italic">
              {t.booking.priceDiscountApplied.replace(
                "{p}",
                quote.discount_percent
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const Booking = () => {
  const { t, lang } = useLang();
  const [range, setRange] = useState(undefined);
  const [bookedRanges, setBookedRanges] = useState([]);
  const [pricing, setPricing] = useState(null); // /api/pricing
  const [quote, setQuote] = useState(null);     // /api/pricing/quote
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    guests: 2,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/availability`)
      .then((res) => setBookedRanges(res.data || []))
      .catch(() => setBookedRanges([]));
    axios
      .get(`${API}/pricing`)
      .then((res) => setPricing(res.data))
      .catch(() => setPricing(null));
  }, []);

  // Fetch a price quote whenever both dates are selected
  useEffect(() => {
    if (!range?.from || !range?.to) {
      setQuote(null);
      return;
    }
    const ci = toYMD(range.from);
    const co = toYMD(range.to);
    setQuoteLoading(true);
    let cancelled = false;
    axios
      .post(`${API}/pricing/quote`, { check_in: ci, check_out: co })
      .then((res) => {
        if (!cancelled) setQuote(res.data);
      })
      .catch(() => {
        if (!cancelled) setQuote(null);
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  // Build disabled dates list for the calendar
  const disabledDates = useMemo(() => {
    const list = [];
    for (const r of bookedRanges) {
      const s = parseYMD(r.start_date);
      const e = parseYMD(r.end_date);
      if (!s || !e) continue;
      list.push(...eachDayInRange(s, e));
    }
    return list;
  }, [bookedRanges]);

  const disabledMatcher = useMemo(
    () => [{ before: new Date() }, ...disabledDates],
    [disabledDates]
  );

  const nights = useMemo(() => {
    if (!range?.from || !range?.to) return 0;
    const ms = range.to.getTime() - range.from.getTime();
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  }, [range]);

  // Show two months only when the calendar column is wide enough (≥1280px viewport).
  const [numberOfMonths, setNumberOfMonths] = useState(1);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1280px)");
    const apply = () => setNumberOfMonths(mq.matches ? 2 : 1);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const handleChange = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!range?.from || !range?.to) {
      toast.error(t.booking.errorPickDates);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        check_in: toYMD(range.from),
        check_out: toYMD(range.to),
        guests: parseInt(form.guests, 10) || 2,
        message: form.message.trim() || null,
        language: lang,
      });
      toast.success(t.booking.successTitle, { description: t.booking.successBody });
      // refresh availability (in case admin auto-confirms in future)
      setRange(undefined);
      setForm({ name: "", email: "", phone: "", guests: 2, message: "" });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) toast.error(t.booking.errorOverlap);
      else toast.error(t.booking.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative bg-[#2C302E] py-24 md:py-32 text-[#F8F5F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.28em] text-[#C05C3D] font-semibold">
            {t.booking.eyebrow}
          </span>
          <h2
            data-testid="booking-title"
            className="mt-6 font-serif font-light text-[#F8F5F0] text-4xl md:text-5xl leading-[1.08] tracking-tight"
          >
            {t.booking.title}
          </h2>
          <p className="mt-6 text-[#F8F5F0]/70 text-base md:text-lg leading-relaxed font-light">
            {t.booking.subtitle}
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Calendar */}
          <div className="lg:col-span-6">
            <div className="bg-[#F8F5F0] text-[#2C302E] p-4 md:p-6 overflow-x-auto">
              <Calendar
                data-testid="booking-calendar"
                mode="range"
                selected={range}
                onSelect={setRange}
                numberOfMonths={numberOfMonths}
                disabled={disabledMatcher}
                locale={LOCALES[lang] || enUS}
                weekStartsOn={1}
                modifiersClassNames={{
                  selected: "!bg-[#C05C3D] !text-[#F8F5F0]",
                  range_start: "!bg-[#C05C3D] !text-[#F8F5F0]",
                  range_end: "!bg-[#C05C3D] !text-[#F8F5F0]",
                  range_middle: "!bg-[#EAE4D9] !text-[#2C302E]",
                  disabled: "line-through opacity-40",
                  today: "ring-1 ring-[#1D4B69]",
                }}
                className="w-full mx-auto"
                classNames={{
                  months: "flex flex-col xl:flex-row gap-6 xl:gap-4 justify-center",
                  month: "space-y-3",
                  caption_label: "font-serif text-base md:text-lg",
                  head_cell:
                    "text-[#5C625E] uppercase tracking-[0.15em] text-[0.6rem] w-8 md:w-9 font-medium",
                  cell: "p-0 text-center text-sm",
                  day: "h-8 w-8 md:h-9 md:w-9 p-0 font-light hover:bg-[#EAE4D9] rounded-none",
                }}
              />
            </div>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/70">
              <span data-testid="legend-available" className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-[#F8F5F0]/20 border border-[#F8F5F0]/40" />
                {t.booking.legendAvailable}
              </span>
              <span data-testid="legend-booked" className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-[#F8F5F0]/10 border border-[#F8F5F0]/30 line-through">
                  <span className="block w-full h-px bg-[#F8F5F0]/60 mt-[5px]" />
                </span>
                {t.booking.legendBooked}
              </span>
              <span data-testid="legend-selected" className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 bg-[#C05C3D]" />
                {t.booking.legendSelected}
              </span>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={submit}
            data-testid="booking-form"
            className="lg:col-span-6 space-y-8"
          >
            <div className="grid grid-cols-2 gap-6 border-b border-[#F8F5F0]/15 pb-6">
              <div>
                <Label className="text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60">
                  {t.booking.checkIn}
                </Label>
                <div
                  data-testid="selected-checkin"
                  className="mt-2 font-serif text-2xl text-[#F8F5F0] min-h-[2rem]"
                >
                  {range?.from
                    ? range.from.toLocaleDateString(lang, {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "—"}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60">
                  {t.booking.checkOut}
                </Label>
                <div
                  data-testid="selected-checkout"
                  className="mt-2 font-serif text-2xl text-[#F8F5F0] min-h-[2rem]"
                >
                  {range?.to
                    ? range.to.toLocaleDateString(lang, {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : "—"}
                </div>
              </div>
              {nights > 0 && (
                <div
                  data-testid="nights-summary"
                  className="col-span-2 text-xs uppercase tracking-[0.24em] text-[#C05C3D] font-semibold"
                >
                  {nights} {t.booking.nights}
                </div>
              )}
            </div>

            {/* Pricing panel */}
            {(pricing || quote) && (
              <PricingPanel
                t={t}
                lang={lang}
                pricing={pricing}
                quote={quote}
                loading={quoteLoading}
                nights={nights}
              />
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <Label
                  htmlFor="bk-name"
                  className="text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60"
                >
                  {t.booking.name}
                </Label>
                <Input
                  id="bk-name"
                  data-testid="input-name"
                  value={form.name}
                  onChange={handleChange("name")}
                  required
                  minLength={2}
                  className="mt-2 bg-transparent border-0 border-b border-[#F8F5F0]/30 rounded-none px-0 text-[#F8F5F0] focus-visible:ring-0 focus-visible:border-[#C05C3D] text-base"
                />
              </div>
              <div>
                <Label
                  htmlFor="bk-email"
                  className="text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60"
                >
                  {t.booking.email}
                </Label>
                <Input
                  id="bk-email"
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={handleChange("email")}
                  required
                  className="mt-2 bg-transparent border-0 border-b border-[#F8F5F0]/30 rounded-none px-0 text-[#F8F5F0] focus-visible:ring-0 focus-visible:border-[#C05C3D] text-base"
                />
              </div>
              <div>
                <Label
                  htmlFor="bk-phone"
                  className="text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60"
                >
                  {t.booking.phone}
                </Label>
                <Input
                  id="bk-phone"
                  data-testid="input-phone"
                  value={form.phone}
                  onChange={handleChange("phone")}
                  className="mt-2 bg-transparent border-0 border-b border-[#F8F5F0]/30 rounded-none px-0 text-[#F8F5F0] focus-visible:ring-0 focus-visible:border-[#C05C3D] text-base"
                />
              </div>
              <div>
                <Label
                  htmlFor="bk-guests"
                  className="text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60"
                >
                  {t.booking.guests}
                </Label>
                <Input
                  id="bk-guests"
                  data-testid="input-guests"
                  type="number"
                  min={1}
                  max={20}
                  value={form.guests}
                  onChange={handleChange("guests")}
                  required
                  className="mt-2 bg-transparent border-0 border-b border-[#F8F5F0]/30 rounded-none px-0 text-[#F8F5F0] focus-visible:ring-0 focus-visible:border-[#C05C3D] text-base"
                />
              </div>
            </div>

            <div>
              <Label
                htmlFor="bk-message"
                className="text-xs uppercase tracking-[0.22em] text-[#F8F5F0]/60"
              >
                {t.booking.message}
              </Label>
              <Textarea
                id="bk-message"
                data-testid="input-message"
                value={form.message}
                onChange={handleChange("message")}
                placeholder={t.booking.messagePlaceholder}
                rows={4}
                className="mt-2 bg-transparent border border-[#F8F5F0]/20 rounded-none text-[#F8F5F0] placeholder:text-[#F8F5F0]/40 focus-visible:ring-0 focus-visible:border-[#C05C3D] text-base"
              />
            </div>

            <button
              type="submit"
              data-testid="booking-submit-button"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-3 bg-[#C05C3D] hover:bg-[#a64c30] disabled:opacity-60 text-[#F8F5F0] px-10 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors min-w-[260px]"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t.booking.submitting}
                </>
              ) : (
                <>
                  {t.booking.submit}
                  <span className="inline-block w-6 h-px bg-current" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};
